import { FilterHelper } from "src/helpers";
import { CreationDateSortOptions } from "src/enums";
import {
  DateTimeRange,
  PostPurchaseOrdersExportsRequestBody,
  PostTransitPurchaseOrdersExportsRequestBody,
  PostPurchaseOrdersInvoiceExportsRequestBody,
  PurchaseOrdersFilters,
  TransitPurchaseOrdersFilters
} from "src/types";

const formatDateOnlyString = (date?: Date) => {
  if (!date) return undefined;
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export class ExportPurchaseOrderMapper {
  public static createPurchaseOrdersExportsRequestBody(
    dateTimeRange?: DateTimeRange,
    filters?: PurchaseOrdersFilters,
    sort?: CreationDateSortOptions,
    deliveryDateTimeRange?: DateTimeRange,
    receiptDateTimeRange?: DateTimeRange
  ): PostPurchaseOrdersExportsRequestBody {
    const createdAtStart = dateTimeRange?.start?.toISOString();
    const createdAtEnd = dateTimeRange?.end?.toISOString();
    const deliveryDateStart = formatDateOnlyString(deliveryDateTimeRange?.start);
    const deliveryDateEnd = formatDateOnlyString(deliveryDateTimeRange?.end);
    const receiptDateStart = formatDateOnlyString(receiptDateTimeRange?.start);
    const receiptDateEnd = formatDateOnlyString(receiptDateTimeRange?.end);

    const completionStatus = FilterHelper.normalisePOCompletionStatusFilter(filters?.completionStatusFilter);
    const confirmationStatus = FilterHelper.normalisePOConfirmationStatusFilter(filters?.confirmationStatusFilter);
    const checkInStatus = filters?.checkInStatusFilter;
    const checkInFlaggedIssue = filters?.checkInFlaggedIssueFilter;

    const hasGxoError = filters?.confirmationStatusFilter === "GXO Error" ? true : undefined;
    const portalAccess =
      filters?.confirmationStatusFilter === "TBC (Non-Portal)" ||
      filters?.confirmationStatusFilter === "Overdue (Non-Portal)"
        ? false
        : undefined;

    const normalisedDateTimeRange = { createdAtStart, createdAtEnd };
    const normalisedFilters = {
      ...filters,
      completionStatus,
      confirmationStatus,
      portalAccess,
      checkInStatus,
      checkInFlaggedIssue,
      hasGxoError,
      deliveryDateStart,
      deliveryDateEnd,
      receiptDateStart,
      receiptDateEnd,
      filterPopoverVisible: undefined,
      completionStatusFilter: undefined,
      checkInFlaggedIssueFilter: undefined,
      checkInStatusFilter: undefined,
      confirmationStatusFilter: undefined
    };

    const requestBody: PostPurchaseOrdersExportsRequestBody = {
      dateTimeRange: normalisedDateTimeRange,
      filters: normalisedFilters,
      sort
    };

    return requestBody;
  }

  public static createTransitPurchaseOrdersExportsRequestBody(
    dateTimeRange?: DateTimeRange,
    filters?: TransitPurchaseOrdersFilters,
    sort?: CreationDateSortOptions
  ): PostTransitPurchaseOrdersExportsRequestBody {
    const createdAtStart = dateTimeRange?.start?.toISOString();
    const createdAtEnd = dateTimeRange?.end?.toISOString();

    const transitStatus = FilterHelper.normaliseTransitStatusFilter(filters?.transitStatusFilter);

    const requestBody: PostTransitPurchaseOrdersExportsRequestBody = {
      dateTimeRange: { createdAtStart, createdAtEnd },
      filters: {
        transitStatus,
        vendorName: filters?.vendorName,
        senderEmail: filters?.senderEmail,
        matchStatus: filters?.matchStatusFilter,
        action: filters?.actionFilter,
        completionStatus: filters?.completionStatusFilter,
        isBackorder: filters?.isBackorderFilter
      },
      sort
    };

    return requestBody;
  }

  public static createPurchaseOrdersInvoiceExportsRequestBody(
    dateTimeRange?: DateTimeRange,
    isProForma?: boolean
  ): PostPurchaseOrdersInvoiceExportsRequestBody {
    const lastModifiedStart = dateTimeRange?.start?.toISOString();
    const lastModifiedEnd = dateTimeRange?.end?.toISOString();

    const requestBody: PostPurchaseOrdersInvoiceExportsRequestBody = {
      dateTimeRange: { lastModifiedStart, lastModifiedEnd },
      isProForma
    };

    return requestBody;
  }
}
